import React, { useCallback, useContext } from "react";

import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import Validator from "validator";

import { Button, Heading, IconCheckCircle, Input, Text } from "@busbud/horizon";

import { Dialog } from "@app/components/dialog";
import { LiteAppContext } from "@app/context/lite-app-context";
import { closeModal } from "@app/store/state/modals";
import { yieldToMain } from "@app/utils/scheduler-yield";

import type { SubmitHandler } from "react-hook-form";

interface Inputs {
  email: string;
}

interface Props {
  handleSubmittedData?: (data: Inputs) => void;
}

export const EmailCollection: React.FC<Props> = props => {
  const { handleSubmittedData } = props;
  const dispatch = useDispatch();
  const { tracker, liteTranslator } = useContext(LiteAppContext);
  const [completed, setCompleted] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<Inputs>();

  const onClose = useCallback(async () => {
    await yieldToMain();
    !completed &&
      tracker?.asyncTrack({
        action: "email_collection_rejected",
        no_amplitude: false
      });
    dispatch(closeModal("email_collection"));
  }, [dispatch, tracker, completed]);

  const onSubmit = useCallback<SubmitHandler<Inputs>>(
    async data => {
      await yieldToMain();
      tracker?.asyncTrack({
        action: "email_collection_submitted",
        gtm_props: { email: data.email, optin_source: "exit-intent-modal" },
        no_amplitude: false
      });
      handleSubmittedData?.(data);
      setCompleted(true);
    },
    [tracker, handleSubmittedData]
  );

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      className="m-[revert] h-fit max-h-fit w-[444px] max-w-[calc(100%-32px)]"
    >
      <Dialog.Header onClose={onClose} />
      <Dialog.Body>
        <div className="flex flex-col items-center justify-center gap-200">
          {!completed && (
            <>
              <div className="mb-100 flex justify-center">
                <img
                  src="https://busbud.imgix.net/modals/email-collection/not-ready-to-book-yet.png"
                  alt=""
                  width={175}
                />
              </div>

              <Heading
                as="p"
                size={{ md: "md", initial: "sm" }}
                className="text-center"
              >
                {liteTranslator.t("!modal.email-collection.title")}
              </Heading>

              <Text
                as="span"
                size={{ md: "lg", initial: "md" }}
                className="text-center text-color-secondary"
              >
                {liteTranslator.t("!modal.email-collection.content")}
              </Text>

              <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                <div className="flex flex-col items-start justify-center gap-150 sm:flex-row">
                  <Input
                    className="w-full grow-[1]"
                    size="sm"
                    isInvalid={!!errors.email}
                    id="email"
                    labelText={liteTranslator.t(
                      "!modal.email-collection.input-label"
                    )}
                    {...register("email", {
                      required: true,
                      validate: email => Validator.isEmail(email)
                    })}
                    errorText={
                      !!errors.email
                        ? liteTranslator.t(
                            "!modal.email-collection.input-error"
                          )
                        : undefined
                    }
                  />
                  <Button
                    size="lg"
                    variant="contained"
                    appearance="primary"
                    // @ts-expect-error TODO: expose type attribute in Button component
                    type="submit"
                    className="w-full sm:w-auto"
                  >
                    {liteTranslator.t("!modal.email-collection.cta")}
                  </Button>
                </div>
              </form>
            </>
          )}

          {!!completed && (
            <>
              <IconCheckCircle
                variant="outlined"
                size="lg"
                className="text-color-scheme-functional-positive-500"
              />
              <Heading
                as="p"
                size={{ md: "md", initial: "sm" }}
                className="mb-100 text-center"
              >
                {liteTranslator.t("!modal.email-collection.thanks")}
              </Heading>
            </>
          )}
        </div>
      </Dialog.Body>
    </Dialog>
  );
};
export default EmailCollection;
